import { _ as _ts_decorate } from "@swc/helpers/_/_ts_decorate";
import { _ as _ts_metadata } from "@swc/helpers/_/_ts_metadata";
import $ from "jquery";
import { action, flow, makeObservable, observable, runInAction, set } from "mobx";
import { getTreeParentById } from "@devowl-wp/react-folder-tree";
import { applyTreeNodePermissionLock } from "../integrations/permissions.js";
import { request } from "../utils/request.js";
import { locationRestFoldersDelete } from "../wp-api/folders.delete.js";
import { locationRestFoldersPut } from "../wp-api/folders.put.js";
class FolderNode {
    id;
    className = {};
    icon = "folder";
    iconActive = "";
    childNodes = [];
    title = "";
    count = 0;
    checked = false;
    selected = false;
    $busy = false;
    $busyOrder = false;
    $droppable = true;
    $visible = true;
    contentCustomOrder = 0;
    forceCustomOrder = false;
    lastOrderBy = "";
    orderAutomatically = false;
    lastSubOrderBy = "";
    subOrderAutomatically = false;
    properties = {};
    parent;
    treeStore;
    constructor(folderNode, treeStore){
        this.treeStore = treeStore;
        makeObservable(this);
        runInAction(()=>{
            set(this, folderNode);
            if (this.id !== undefined && this.id !== null) {
                treeStore.refs.set(this.id, this);
            }
        });
    }
    static mapFromRestEndpoint({ id, name, cnt, children, contentCustomOrder, forceCustomOrder, lastOrderBy, orderAutomatically, lastSubOrderBy, subOrderAutomatically, ...rest }, parent) {
        const node = new FolderNode({
            id,
            title: name,
            count: cnt,
            icon: "folder",
            childNodes: [],
            className: {},
            contentCustomOrder: contentCustomOrder ?? 0,
            forceCustomOrder: !!forceCustomOrder,
            lastOrderBy: lastOrderBy ?? "",
            orderAutomatically: !!orderAutomatically,
            lastSubOrderBy: lastSubOrderBy ?? "",
            subOrderAutomatically: !!subOrderAutomatically,
            $visible: true,
            properties: rest,
            parent
        }, this);
        switch(node.properties.type){
            case 0:
                node.iconActive = "folder-open";
                break;
            case 1:
                node.icon = "collection";
                break;
            case 2:
                node.icon = "gallery";
                break;
            default:
                break;
        }
        applyTreeNodePermissionLock(node);
        if (children?.length) {
            node.childNodes = children.map((child)=>FolderNode.mapFromRestEndpoint.call(this, child, node));
        }
        return node;
    }
    get parentArray() {
        return this.parent ? this.parent.childNodes : this.treeStore.tree;
    }
    get nextSibling() {
        const { parentArray } = this;
        const indexOf = parentArray.indexOf(this);
        const nextOf = indexOf + 1;
        return indexOf > -1 ? parentArray[nextOf] : undefined;
    }
    get path() {
        const ancestors = [];
        let { parent } = this;
        while(parent){
            ancestors.push(parent);
            ({ parent } = parent);
        }
        return [
            ...ancestors.reverse(),
            this
        ];
    }
    get parentId() {
        return getTreeParentById(this.id, this.treeStore.tree, this.treeStore.rootId);
    }
    setter(callback) {
        callback(this);
    }
    toggleChecked(flag = !this.checked, children = true) {
        this.checked = flag;
        if (children) {
            this.childNodes.forEach((n)=>n.toggleChecked(flag));
        }
        if (!flag) {
            const ancestors = [
                ...this.path
            ];
            ancestors.pop();
            ancestors.forEach((n)=>n.toggleChecked(false, false));
        }
    }
    setName = flow(function*(inputValue) {
        this.$busy = true;
        try {
            const result = yield request({
                location: locationRestFoldersPut,
                params: {
                    id: +this.id
                },
                request: {
                    name: inputValue
                }
            });
            this.title = result.name;
            this.properties = $.extend(true, {}, this.properties, result);
            return result;
        } finally{
            this.$busy = false;
        }
    });
    applyOrder = flow(function*(id, automatically = false) {
        if (process.env.PLUGIN_CTX !== "pro") {
            return false;
        }
    /* Silence is golden... Until you have PRO Version! */ });
    applyChildrenOrder = flow(function*(id, automatically = false) {
        if (process.env.PLUGIN_CTX !== "pro") {
            return false;
        }
    /* Silence is golden... Until you have PRO Version! */ });
    visible(state) {
        this.$visible = state;
    }
    trash = flow(function*() {
        this.$busy = true;
        try {
            yield request({
                location: locationRestFoldersDelete,
                params: {
                    id: +this.id
                }
            });
            this.visible(false);
        } finally{
            this.$busy = false;
        }
    });
    addChildNode(node) {
        node.parent = this;
        this.childNodes.push(node);
    }
    setSelected(state) {
        if (state) {
            this.selected = true;
            if (this.treeStore.selected !== this) {
                this.treeStore.setSelected(this);
            }
            return;
        }
        if (!this.selected) {
            return;
        }
        this.selected = false;
    }
    toJSON() {
        return {
            id: this.id,
            className: this.className,
            icon: this.icon,
            iconActive: this.iconActive,
            childNodes: this.childNodes.map((c)=>c.toJSON()),
            title: this.title,
            count: this.count,
            checked: this.checked,
            selected: this.selected,
            $busy: this.$busy,
            $busyOrder: this.$busyOrder,
            $droppable: this.$droppable,
            $visible: this.$visible,
            contentCustomOrder: this.contentCustomOrder,
            forceCustomOrder: this.forceCustomOrder,
            lastOrderBy: this.lastOrderBy,
            orderAutomatically: this.orderAutomatically,
            lastSubOrderBy: this.lastSubOrderBy,
            subOrderAutomatically: this.subOrderAutomatically,
            properties: this.properties
        };
    }
}
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], FolderNode.prototype, "id", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", typeof Record === "undefined" ? Object : Record)
], FolderNode.prototype, "className", void 0);
_ts_decorate([
    observable
], FolderNode.prototype, "icon", void 0);
_ts_decorate([
    observable
], FolderNode.prototype, "iconActive", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Array)
], FolderNode.prototype, "childNodes", void 0);
_ts_decorate([
    observable
], FolderNode.prototype, "title", void 0);
_ts_decorate([
    observable
], FolderNode.prototype, "count", void 0);
_ts_decorate([
    observable
], FolderNode.prototype, "checked", void 0);
_ts_decorate([
    observable
], FolderNode.prototype, "selected", void 0);
_ts_decorate([
    observable
], FolderNode.prototype, "$busy", void 0);
_ts_decorate([
    observable
], FolderNode.prototype, "$busyOrder", void 0);
_ts_decorate([
    observable
], FolderNode.prototype, "$droppable", void 0);
_ts_decorate([
    observable
], FolderNode.prototype, "$visible", void 0);
_ts_decorate([
    observable
], FolderNode.prototype, "contentCustomOrder", void 0);
_ts_decorate([
    observable
], FolderNode.prototype, "forceCustomOrder", void 0);
_ts_decorate([
    observable
], FolderNode.prototype, "lastOrderBy", void 0);
_ts_decorate([
    observable
], FolderNode.prototype, "orderAutomatically", void 0);
_ts_decorate([
    observable
], FolderNode.prototype, "lastSubOrderBy", void 0);
_ts_decorate([
    observable
], FolderNode.prototype, "subOrderAutomatically", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", typeof Record === "undefined" ? Object : Record)
], FolderNode.prototype, "properties", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], FolderNode.prototype, "parent", void 0);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        Function
    ]),
    _ts_metadata("design:returntype", void 0)
], FolderNode.prototype, "setter", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        void 0,
        void 0
    ]),
    _ts_metadata("design:returntype", void 0)
], FolderNode.prototype, "toggleChecked", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        Boolean
    ]),
    _ts_metadata("design:returntype", void 0)
], FolderNode.prototype, "visible", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        Object
    ]),
    _ts_metadata("design:returntype", void 0)
], FolderNode.prototype, "addChildNode", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        Boolean
    ]),
    _ts_metadata("design:returntype", void 0)
], FolderNode.prototype, "setSelected", null);
export { FolderNode };
